import {ActivityHeader} from '@github-ui/commenting/ActivityHeader'
import type {ConversationIssueComment} from '../../page-data/payloads/conversation-comments'
import {useReactToIssueCommentMutation} from '../../mutations/use-react-to-issue-comment-mutation'
import {type SelectionContext, selectQuoteFromComment} from '@github-ui/commenting/quotes'
import {use, useRef} from 'react'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useLayoutCanComment} from '../../stores/layout-selectors'
import {CommentLayout} from './CommentLayout'
import {ConversationItemActions, ConversationItemActionsProvider} from './ConversationItemActions'
import {CommentsContext} from './CommentsContext'
import {issueCommentAnchor} from './utils/comment-anchors'
import {useFocusHashTarget} from '../../hooks/use-focus-hash-target'
import {useUpdateIssueCommentMutation} from '../../mutations/use-update-issue-comment-mutation'
import {useMergedRefs} from '@primer/react'
import {announce} from '@github-ui/aria-live'
import {useBodyEditManager} from './use-body-edit-manager'
import {useEditorFocus} from './use-editor-focus'
import {ConversationItemBody} from './ConversationItemBody'

interface IssueCommentItemProps {
  comment: ConversationIssueComment
  pullRequestAuthorLogin: string
  // Optional callback for handling quote reply actions. If provided, a "Quote reply" button will be rendered.
  onQuoteReply?: (quotedText: string) => void
  className?: string
}

export function IssueCommentItem({
  comment,
  pullRequestAuthorLogin,
  onQuoteReply: externalOnQuoteReply,
  className,
}: IssueCommentItemProps): React.ReactElement | null {
  const currentUserCanComment = useLayoutCanComment()

  const commentRef = useRef<HTMLElement>(null)

  const anchorPrefix = use(CommentsContext).commentAnchorPrefix
  const id = issueCommentAnchor(String(comment.id), anchorPrefix)
  useFocusHashTarget(id)

  const reactToCommentMutation = useReactToIssueCommentMutation()

  const onQuoteReply =
    currentUserCanComment && externalOnQuoteReply
      ? () => {
          const current = ssrSafeWindow?.getSelection()
          let currentSelection: SelectionContext | null = null

          if (current && current.anchorNode && current.rangeCount > 0) {
            currentSelection = {
              anchorNode: current.anchorNode,
              range: current.getRangeAt(0),
            }
          }

          const quotedText = selectQuoteFromComment(commentRef.current, currentSelection, comment.body)
          externalOnQuoteReply(quotedText ?? comment.body)
        }
      : undefined

  const editBodyMutation = useUpdateIssueCommentMutation({
    onSuccess: () => announce('Comment updated successfully.'),
  })
  const directEditBodyMutation = useUpdateIssueCommentMutation()
  const editManager = useBodyEditManager(comment, editBodyMutation, directEditBodyMutation)

  const {editorRef, editorContainerRef, returnFocusRef} = useEditorFocus({autofocus: true})
  const combinedCommentRef = useMergedRefs(commentRef, returnFocusRef)

  if (!comment.repository) return null

  return (
    <ConversationItemActionsProvider onQuoteReply={onQuoteReply}>
      <CommentLayout
        id={id}
        ref={combinedCommentRef}
        className={className}
        tabIndex={-1}
        header={
          <ActivityHeader
            avatarUrl={comment.author?.avatarUrl || ''}
            comment={{
              ...comment,
              id: String(comment.id),
              repository: comment.repository,
            }}
            commentAuthorLogin={comment.author?.login ?? ''}
            commentSubjectType="pull request"
            commentSubjectAuthorLogin={pullRequestAuthorLogin}
            forceInlineAvatar
            isMinimized={false}
            noContainerStyles
            actions={
              editManager.isEditing ? (
                <>{/* hide comment actions while editing */}</>
              ) : (
                <ConversationItemActions
                  onQuoteReply={onQuoteReply}
                  link={comment.url}
                  onEdit={editManager.startEditIfAllowed}
                />
              )
            }
          />
        }
      >
        <ConversationItemBody
          item={comment}
          reactMutation={reactToCommentMutation}
          editManager={editManager}
          editorRef={editorRef}
          editorContainerRef={editorContainerRef}
        />
      </CommentLayout>
    </ConversationItemActionsProvider>
  )
}
